import { useState, useEffect, useMemo } from 'react';
import { supabase } from './lib/supabase';
import { useInvoices, useExpenses, useClients, useBizInfo, useBills } from './lib/useData';
import Auth from './components/Auth';
import { downloadInvoicePDF, emailInvoicePDF } from './lib/invoicePDF';

// ─── Helpers ───
const fmt = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
const today = () => new Date().toISOString().split('T')[0];
const qtr = (d) => { const m = new Date(d).getMonth(); if (m < 3) return 'Q3'; if (m < 6) return 'Q4'; if (m < 9) return 'Q1'; return 'Q2'; };
const fy = (d) => { const dt = new Date(d); const y = dt.getFullYear(); const m = dt.getMonth(); return m < 6 ? `${y - 1}-${y}` : `${y}-${y + 1}`; };

const EXPENSE_CATS = [
  'Camera & Gear', 'Printing & Materials', 'Software & Subscriptions',
  'Travel & Transport', 'Office & Supplies', 'Marketing & Advertising',
  'Insurance', 'Professional Development', 'Phone & Internet', 'Other'
];
const INCOME_TYPES = ['Consulting', 'Photography', 'Print Sales', 'Other'];
const PAYMENT_ACCOUNTS = ['Credit Card', 'Cash'];
const TABS = ['dashboard', 'invoices', 'bills', 'expenses', 'clients', 'reports'];

const exportCSV = (rows, filename) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => `"${(r[k] ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ─── Main App ───
export default function App() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f1117', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8a8580' }}>
        Loading...
      </div>
    );
  }

  if (!session) return <Auth />;

  return <MainApp session={session} />;
}

function MainApp({ session }) {
  const [tab, setTab] = useState('dashboard');
  const [showSettings, setShowSettings] = useState(false);

  const { data: invoices, loading: invLoading, add: addInvoice, update: updateInvoice, remove: deleteInvoice } = useInvoices();
  const { data: expenses, loading: expLoading, add: addExpense, remove: deleteExpense } = useExpenses();
  const { data: clients, loading: cliLoading, add: addClient, update: updateClient, remove: deleteClient } = useClients();
  const { data: bills, loading: billLoading, add: addBill, update: updateBill, remove: deleteBill } = useBills();
  const { bizInfo, saveBizInfo } = useBizInfo();

  const [localBiz, setLocalBiz] = useState(bizInfo);
  useEffect(() => { setLocalBiz(bizInfo); }, [bizInfo]);

  const handleSignOut = async () => { await supabase.auth.signOut(); };

  const loading = invLoading || expLoading || cliLoading || billLoading;

  return (
    <div style={{ minHeight: '100vh', background: '#f7f5f2', color: '#1a1a1a', fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet" />
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input, select, textarea { font-family: 'DM Sans', 'Helvetica Neue', sans-serif; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #c4b9ab; border-radius: 3px; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in { animation: fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1); }
        .card { background: #fefdfb; border: 1px solid rgba(0,0,0,0.06); border-radius: 12px; padding: 20px; transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); box-shadow: 0 2px 12px rgba(0,0,0,0.04); }
        .card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.07); }
        .btn { padding: 8px 18px; border-radius: 50px; border: none; cursor: pointer; font-weight: 500; font-size: 13px; letter-spacing: 0.03em; transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); font-family: inherit; }
        .btn-primary { background: #1a1a1a; color: #f0ece6; }
        .btn-primary:hover { background: #c8956c; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(200,149,108,0.3); }
        .btn-ghost { background: transparent; color: #3a3a3a; border: 1px solid rgba(0,0,0,0.12); }
        .btn-ghost:hover { border-color: #c8956c; color: #c8956c; transform: translateY(-1px); }
        .btn-danger { background: #c85c5c; color: white; }
        .btn-danger:hover { background: #a84a4a; }
        .btn-sm { padding: 5px 12px; font-size: 12px; }
        .input { background: #fefdfb; border: 1px solid rgba(0,0,0,0.1); border-radius: 8px; padding: 10px 14px; color: #1a1a1a; font-size: 14px; width: 100%; outline: none; transition: border-color 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        .input:focus { border-color: #c8956c; box-shadow: 0 0 0 3px rgba(200,149,108,0.1); }
        .badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-family: 'Space Mono', monospace; font-size: 10px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.1em; }
        .badge-paid { background: rgba(74,124,138,0.12); color: #4a7c8a; }
        .badge-unpaid { background: rgba(200,149,108,0.15); color: #b5825b; }
        .badge-overdue { background: rgba(200,100,80,0.12); color: #c85c5c; }
        .badge-draft { background: rgba(0,0,0,0.05); color: #8a8580; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; padding: 10px 12px; font-family: 'Space Mono', monospace; font-size: 10px; text-transform: uppercase; letter-spacing: 0.12em; color: #8a8580; border-bottom: 1px solid rgba(0,0,0,0.08); font-weight: 400; }
        td { padding: 12px; border-bottom: 1px solid rgba(0,0,0,0.04); font-size: 14px; color: #252525; }
        tr:hover td { background: rgba(196,185,171,0.08); }
        .mono { font-family: 'Space Mono', monospace; }
        .display-font { font-family: 'Cormorant Garamond', Georgia, serif; }
        .tab { padding: 0.5rem 1.25rem; border-radius: 50px; border: 1px solid rgba(0,0,0,0.08); cursor: pointer; font-weight: 400; font-size: 13px; letter-spacing: 0.03em; transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); font-family: inherit; background: transparent; color: #3a3a3a; white-space: nowrap; flex-shrink: 0; }
        .tab:hover { border-color: #c8956c; color: #c8956c; }
        .tab.active { background: #1a1a1a; color: #f0ece6; border-color: #1a1a1a; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(10,10,10,0.5); z-index: 100; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(8px); }
        .modal { background: #fefdfb; border: 1px solid rgba(0,0,0,0.06); border-radius: 16px; padding: 32px; width: 90%; max-width: 560px; max-height: 85vh; overflow-y: auto; box-shadow: 0 24px 80px rgba(0,0,0,0.15); }
      `}</style>

      {/* Header */}
      <header style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fefdfb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400, fontSize: 18, letterSpacing: '-0.01em', color: '#1a1a1a' }}>
              {bizInfo.name || 'Sole Trader Books'}
            </div>
            {bizInfo.abn && <div style={{ fontSize: 10, color: '#8a8580', fontFamily: "'Space Mono', monospace", letterSpacing: '0.1em' }}>ABN {bizInfo.abn}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#8a8580', fontFamily: "'Space Mono', monospace", letterSpacing: '0.05em' }}>{session.user.email}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowSettings(true)}>Settings</button>
          <button className="btn btn-ghost btn-sm" onClick={handleSignOut}>Sign Out</button>
        </div>
      </header>

      {/* Nav */}
      <nav style={{ padding: '16px 24px', display: 'flex', gap: 8, borderBottom: '1px solid rgba(0,0,0,0.04)', background: '#fefdfb', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <style>{`nav::-webkit-scrollbar { display: none; }`}</style>
        {TABS.map(t => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </nav>

      {/* Content */}
      <main style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }} className="fade-in" key={tab}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#8a8580' }}>Loading your data...</div>
        ) : (
          <>
            {tab === 'dashboard' && <Dashboard invoices={invoices} expenses={expenses} />}
            {tab === 'invoices' && <Invoices invoices={invoices} clients={clients} bizInfo={bizInfo} addInvoice={addInvoice} updateInvoice={updateInvoice} deleteInvoice={deleteInvoice} />}
            {tab === 'bills' && <Bills bills={bills} addBill={addBill} updateBill={updateBill} deleteBill={deleteBill} addExpense={addExpense} />}
            {tab === 'expenses' && <Expenses expenses={expenses} addExpense={addExpense} deleteExpense={deleteExpense} />}
            {tab === 'clients' && <Clients clients={clients} addClient={addClient} updateClient={updateClient} deleteClient={deleteClient} invoices={invoices} />}
            {tab === 'reports' && <Reports invoices={invoices} expenses={expenses} />}
          </>
        )}
      </main>

      {/* Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 400, marginBottom: 20, color: '#1a1a1a' }}>Business Details</h2>
            {[['Business Name', 'name'], ['ABN', 'abn'], ['Email', 'email'], ['Phone', 'phone'], ['Address', 'address']].map(([label, key]) => (
              <div key={key} style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, color: '#8a8580', marginBottom: 4, display: 'block', fontFamily: "'Space Mono', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</label>
                <input className="input" value={localBiz[key] || ''} onChange={e => setLocalBiz(p => ({ ...p, [key]: e.target.value }))} />
              </div>
            ))}

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 18, fontWeight: 400, marginBottom: 14, color: '#1a1a1a' }}>Bank / Payment Details</h3>
              <p style={{ fontSize: 12, color: '#8a8580', marginBottom: 14 }}>These appear on your PDF invoices so clients know where to pay.</p>
              {[['Bank Name', 'bank_name'], ['Account Name', 'account_name'], ['BSB', 'bsb'], ['Account Number', 'account_number']].map(([label, key]) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, color: '#8a8580', marginBottom: 4, display: 'block', fontFamily: "'Space Mono', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</label>
                  <input className="input" value={localBiz[key] || ''} onChange={e => setLocalBiz(p => ({ ...p, [key]: e.target.value }))} />
                </div>
              ))}
            </div>

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 18, fontWeight: 400, marginBottom: 14, color: '#1a1a1a' }}>Invoice Logo</h3>
              <p style={{ fontSize: 12, color: '#8a8580', marginBottom: 14 }}>URL to your logo image (PNG). Appears top-right on PDF invoices.</p>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, color: '#8a8580', marginBottom: 4, display: 'block', fontFamily: "'Space Mono', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' }}>Logo URL</label>
                <input className="input" placeholder="https://..." value={localBiz.logo_url || ''} onChange={e => setLocalBiz(p => ({ ...p, logo_url: e.target.value }))} />
              </div>
              {localBiz.logo_url && (
                <div style={{ marginBottom: 8 }}>
                  <img src={localBiz.logo_url} alt="Logo preview" style={{ maxHeight: 50, maxWidth: 150, background: '#fff', padding: 6, borderRadius: 6 }} onError={e => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button className="btn btn-primary" onClick={() => { saveBizInfo(localBiz); setShowSettings(false); }}>Save</button>
              <button className="btn btn-ghost" onClick={() => setShowSettings(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Dashboard
// ═══════════════════════════════════════════════════════
function Dashboard({ invoices, expenses }) {
  const currentFY = fy(today());
  const fyInvoices = invoices.filter(i => fy(i.date) === currentFY && i.status === 'paid');
  const fyExpenses = expenses.filter(e => fy(e.date) === currentFY);
  const totalIncome = fyInvoices.reduce((s, i) => s + Number(i.total), 0);
  const totalExpenses = fyExpenses.reduce((s, e) => s + Number(e.amount), 0);
  const profit = totalIncome - totalExpenses;
  const outstanding = invoices.filter(i => i.status === 'unpaid' || i.status === 'overdue').reduce((s, i) => s + Number(i.total), 0);
  const overdueCount = invoices.filter(i => i.status === 'overdue' || (i.status === 'unpaid' && i.due_date < today())).length;

  const byType = {};
  fyInvoices.forEach(i => { byType[i.income_type] = (byType[i.income_type] || 0) + Number(i.total); });

  return (
    <div>
      <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 28, fontWeight: 400, marginBottom: 4 }}>Dashboard</h1>
      <p style={{ color: '#8a8580', fontSize: 13, marginBottom: 20 }}>Financial year {currentFY}</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Income (Paid)', value: fmt(totalIncome), color: '#4a7c8a' },
          { label: 'Expenses', value: fmt(totalExpenses), color: '#c8956c' },
          { label: 'Net Profit', value: fmt(profit), color: profit >= 0 ? '#10b981' : '#ef4444' },
          { label: 'Outstanding', value: fmt(outstanding), color: '#4a7c8a', sub: overdueCount > 0 ? `${overdueCount} overdue` : null },
        ].map((kpi, idx) => (
          <div className="card" key={idx}>
            <div style={{ fontSize: 12, color: '#8a8580', marginBottom: 8 }}>{kpi.label}</div>
            <div className="mono" style={{ fontSize: 22, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
            {kpi.sub && <div style={{ fontSize: 11, color: '#c85c5c', marginTop: 4 }}>{kpi.sub}</div>}
          </div>
        ))}
      </div>

      {Object.keys(byType).length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Income by Type</h3>
          {Object.entries(byType).sort((a, b) => b[1] - a[1]).map(([type, amount]) => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ width: 100, fontSize: 13, color: '#8a8580' }}>{type}</div>
              <div style={{ flex: 1, background: '#f0ece6', borderRadius: 4, height: 20, overflow: 'hidden' }}>
                <div style={{ width: `${(amount / totalIncome) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #c8956c, #d4896a)', borderRadius: 4 }} />
              </div>
              <div className="mono" style={{ fontSize: 13, width: 100, textAlign: 'right' }}>{fmt(amount)}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Recent Invoices</h3>
          {invoices.slice(0, 5).length === 0 ? <p style={{ color: '#8a8580', fontSize: 13 }}>No invoices yet</p> :
            invoices.slice(0, 5).map(i => (
              <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{i.client_name}</div>
                  <div style={{ fontSize: 11, color: '#8a8580' }}>{i.number}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="mono" style={{ fontSize: 13 }}>{fmt(i.total)}</div>
                  <span className={`badge badge-${i.status === 'paid' ? 'paid' : i.due_date < today() ? 'overdue' : i.status}`}>
                    {i.due_date < today() && i.status === 'unpaid' ? 'overdue' : i.status}
                  </span>
                </div>
              </div>
            ))
          }
        </div>
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Recent Expenses</h3>
          {expenses.slice(0, 5).length === 0 ? <p style={{ color: '#8a8580', fontSize: 13 }}>No expenses yet</p> :
            expenses.slice(0, 5).map(e => (
              <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{e.description}</div>
                  <div style={{ fontSize: 11, color: '#8a8580' }}>{e.category}</div>
                </div>
                <div className="mono" style={{ fontSize: 13 }}>{fmt(e.amount)}</div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Invoices
// ═══════════════════════════════════════════════════════
function Invoices({ invoices, clients, bizInfo, addInvoice, updateInvoice, deleteInvoice }) {
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState(null);

  const nextNumber = useMemo(() => {
    const nums = invoices.map(i => parseInt(i.number?.replace('INV-', '') || '0')).filter(n => !isNaN(n));
    return `INV-${String((Math.max(0, ...nums) + 1)).padStart(4, '0')}`;
  }, [invoices]);

  const openNew = () => {
    setForm({
      number: nextNumber, client_name: '', client_email: '', contact_name: '',
      date: today(), due_date: '', income_type: 'Consulting',
      items: [{ desc: '', qty: 1, rate: 0 }], notes: '', status: 'unpaid'
    });
    setShowForm(true);
  };

  const openEdit = (inv) => {
    setForm({ ...inv, items: inv.items || [{ desc: '', qty: 1, rate: 0 }] });
    setShowForm(true);
  };

  const saveForm = async (statusOverride) => {
    const total = form.items.reduce((s, it) => s + it.qty * it.rate, 0);
    const inv = { ...form, total };
    if (statusOverride) inv.status = statusOverride;

    if (form.id) {
      await updateInvoice(form.id, inv);
    } else {
      await addInvoice(inv);
    }
    setShowForm(false);
    setForm(null);
  };

  const filtered = filter === 'all' ? invoices : invoices.filter(i => {
    if (filter === 'overdue') return i.status === 'unpaid' && i.due_date < today();
    return i.status === filter;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 28, fontWeight: 400 }}>Invoices</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCSV(invoices.map(i => ({ Number: i.number, Client: i.client_name, Date: i.date, Due: i.due_date, Type: i.income_type, Total: i.total, Status: i.status })), 'invoices.csv')}>Export CSV</button>
          <button className="btn btn-primary" onClick={openNew}>+ New Invoice</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {['all', 'unpaid', 'paid', 'overdue', 'draft'].map(f => (
          <button key={f} className={`tab ${filter === f ? 'active' : ''}`} style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table>
          <thead><tr><th>Invoice</th><th>Client</th><th>Type</th><th>Date</th><th>Due</th><th style={{ textAlign: 'right' }}>Amount</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: '#8a8580', padding: 40 }}>No invoices found</td></tr>
            ) : filtered.map(inv => {
              const isOverdue = inv.status === 'unpaid' && inv.due_date < today();
              const st = isOverdue ? 'overdue' : inv.status;
              return (
                <tr key={inv.id}>
                  <td className="mono" style={{ fontWeight: 500 }}>{inv.number}</td>
                  <td>{inv.client_name}</td>
                  <td style={{ color: '#8a8580', fontSize: 13 }}>{inv.income_type}</td>
                  <td style={{ color: '#8a8580', fontSize: 13 }}>{inv.date}</td>
                  <td style={{ color: '#8a8580', fontSize: 13 }}>{inv.due_date}</td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(inv.total)}</td>
                  <td><span className={`badge badge-${st}`}>{st}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button className="btn btn-ghost btn-sm" style={{ color: '#4a7c8a' }} onClick={() => downloadInvoicePDF(inv, bizInfo)} title="Download PDF">PDF</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: '#4a7c8a' }} onClick={() => emailInvoicePDF(inv, bizInfo)} title="Email invoice">Email</button>
                      {inv.status === 'unpaid' && <button className="btn btn-ghost btn-sm" onClick={() => updateInvoice(inv.id, { status: 'paid', paid_date: today() })}>Mark Paid</button>}
                      {inv.status === 'paid' && <button className="btn btn-ghost btn-sm" onClick={() => updateInvoice(inv.id, { status: 'unpaid', paid_date: null })}>Mark Unpaid</button>}
                      {inv.status === 'draft' && <button className="btn btn-ghost btn-sm" onClick={() => updateInvoice(inv.id, { status: 'unpaid' })}>Send</button>}
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(inv)}>Edit</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: '#c85c5c' }} onClick={async () => { if (window.confirm('Delete this invoice?')) await deleteInvoice(inv.id); }}>×</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && form && (() => {
        const total = form.items.reduce((s, it) => s + it.qty * it.rate, 0);
        const canSave = form.client_name && form.date && form.due_date && form.items.some(it => it.desc && it.rate > 0);
        const reqStyle = (val) => !val ? { borderColor: '#d4896a' } : {};
        return (
        <div className="modal-overlay" onClick={() => { setShowForm(false); setForm(null); }}>
          <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 400, marginBottom: 20 }}>{form.id ? 'Edit Invoice' : 'New Invoice'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Invoice # *</label>
                <input className="input" style={reqStyle(form.number)} value={form.number} onChange={e => setForm(p => ({ ...p, number: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Income Type</label>
                <select className="input" value={form.income_type} onChange={e => setForm(p => ({ ...p, income_type: e.target.value }))}>
                  {INCOME_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Client Name *</label>
                <input className="input" style={reqStyle(form.client_name)} list="client-list" value={form.client_name} onChange={e => {
                  const name = e.target.value;
                  const match = clients.find(c => c.name === name);
                  setForm(p => ({ ...p, client_name: name, client_email: match?.email || p.client_email, contact_name: match?.contact_name || p.contact_name }));
                }} />
                <datalist id="client-list">{clients.map(c => <option key={c.id} value={c.name} />)}</datalist>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Key Contact</label>
                <input className="input" value={form.contact_name || ''} placeholder="For invoice Attention line" onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Client Email</label>
                <input className="input" value={form.client_email} onChange={e => setForm(p => ({ ...p, client_email: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Date *</label>
                <input className="input" type="date" style={reqStyle(form.date)} value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Due Date *</label>
                <input className="input" type="date" style={reqStyle(form.due_date)} value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 8, display: 'block' }}>Line Items *</label>
              {form.items.map((item, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 60px 100px 32px', gap: 8, marginBottom: 6 }}>
                  <input className="input" placeholder="Description" value={item.desc} onChange={e => { const items = [...form.items]; items[idx] = { ...items[idx], desc: e.target.value }; setForm(p => ({ ...p, items })); }} />
                  <input className="input" type="number" placeholder="Qty" value={item.qty} onChange={e => { const items = [...form.items]; items[idx] = { ...items[idx], qty: Number(e.target.value) }; setForm(p => ({ ...p, items })); }} />
                  <input className="input" type="number" placeholder="Rate" value={item.rate} onChange={e => { const items = [...form.items]; items[idx] = { ...items[idx], rate: Number(e.target.value) }; setForm(p => ({ ...p, items })); }} />
                  <button className="btn btn-ghost btn-sm" style={{ color: '#c85c5c' }} onClick={() => { if (form.items.length > 1) setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) })); }}>×</button>
                </div>
              ))}
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 4 }} onClick={() => setForm(p => ({ ...p, items: [...p.items, { desc: '', qty: 1, rate: 0 }] }))}>+ Add Line</button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Notes / Reference</label>
              <textarea className="input" rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>

            {/* Sent checkbox */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#3a3a3a', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.sent || false} onChange={e => setForm(p => ({ ...p, sent: e.target.checked }))} style={{ width: 16, height: 16, accentColor: '#c8956c' }} />
                Invoice has been sent to client
              </label>
            </div>

            <div style={{ textAlign: 'right', marginBottom: 16, padding: '12px 0', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <span style={{ color: '#8a8580', fontSize: 13 }}>Total: </span>
              <span className="mono" style={{ fontSize: 22, fontWeight: 700, color: '#4a7c8a' }}>{fmt(total)}</span>
            </div>

            {!canSave && <p style={{ fontSize: 12, color: '#d4896a', marginBottom: 12 }}>Fill in all required fields (*) to save as an invoice. You can still save as a draft.</p>}

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" disabled={!canSave} style={!canSave ? { opacity: 0.4, cursor: 'not-allowed' } : {}} onClick={() => canSave && saveForm()}>Save Invoice</button>
              <button className="btn btn-ghost" onClick={() => saveForm('draft')}>Save as Draft</button>
              <button className="btn btn-ghost" onClick={() => { setShowForm(false); setForm(null); }}>Cancel</button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Expenses
// ═══════════════════════════════════════════════════════
function Expenses({ expenses, addExpense, deleteExpense }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ description: '', amount: '', category: EXPENSE_CATS[0], date: today(), paid_via: PAYMENT_ACCOUNTS[0], notes: '' });
  const [filterCat, setFilterCat] = useState('all');
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.description) e.description = true;
    if (!form.amount || Number(form.amount) <= 0) e.amount = true;
    if (!form.date) e.date = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveExpense = async () => {
    if (!validate()) return;
    await addExpense({ ...form, amount: Number(form.amount) });
    setForm({ description: '', amount: '', category: EXPENSE_CATS[0], date: today(), paid_via: PAYMENT_ACCOUNTS[0], notes: '' });
    setErrors({});
    setShowForm(false);
  };

  const filtered = filterCat === 'all' ? expenses : expenses.filter(e => e.category === filterCat);
  const catTotals = {};
  expenses.forEach(e => { catTotals[e.category] = (catTotals[e.category] || 0) + Number(e.amount); });
  const reqStyle = (key) => errors[key] ? { borderColor: '#d4896a' } : {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 28, fontWeight: 400 }}>Expenses</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCSV(expenses.map(e => ({ Date: e.date, Description: e.description, Category: e.category, 'Paid Via': e.paid_via || '', Amount: e.amount, Notes: e.notes || '' })), 'expenses.csv')}>Export CSV</button>
          <button className="btn btn-primary" onClick={() => { setShowForm(true); setErrors({}); }}>+ Add Expense</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className={`tab ${filterCat === 'all' ? 'active' : ''}`} style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => setFilterCat('all')}>All</button>
        {Object.keys(catTotals).sort().map(cat => (
          <button key={cat} className={`tab ${filterCat === cat ? 'active' : ''}`} style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => setFilterCat(cat)}>
            {cat} <span className="mono" style={{ fontSize: 11, opacity: 0.7 }}>({fmt(catTotals[cat])})</span>
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table>
          <thead><tr><th>Date</th><th>Description</th><th>Category</th><th>Paid Via</th><th style={{ textAlign: 'right' }}>Amount</th><th></th></tr></thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#8a8580', padding: 40 }}>No expenses recorded</td></tr>
            ) : filtered.map(exp => (
              <tr key={exp.id}>
                <td style={{ color: '#8a8580', fontSize: 13 }}>{exp.date}</td>
                <td style={{ fontWeight: 500 }}>{exp.description}</td>
                <td style={{ color: '#8a8580', fontSize: 13 }}>{exp.category}</td>
                <td style={{ color: '#8a8580', fontSize: 13 }}>{exp.paid_via || '—'}</td>
                <td className="mono" style={{ textAlign: 'right', fontWeight: 600, color: '#c8956c' }}>{fmt(exp.amount)}</td>
                <td><button className="btn btn-ghost btn-sm" style={{ color: '#c85c5c' }} onClick={async () => { if (window.confirm('Delete?')) await deleteExpense(exp.id); }}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 400, marginBottom: 20 }}>Add Expense</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Description *</label>
                <input className="input" style={reqStyle('description')} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Amount ($) *</label>
                <input className="input" type="number" step="0.01" style={reqStyle('amount')} value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Category</label>
                <select className="input" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  {EXPENSE_CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Date *</label>
                <input className="input" type="date" style={reqStyle('date')} value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Paid Via</label>
                <select className="input" value={form.paid_via} onChange={e => setForm(p => ({ ...p, paid_via: e.target.value }))}>
                  {PAYMENT_ACCOUNTS.map(a => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Notes</label>
                <input className="input" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
            {Object.keys(errors).length > 0 && <p style={{ fontSize: 12, color: '#d4896a', marginBottom: 12 }}>Please fill in all required fields (*).</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={saveExpense}>Save Expense</button>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Clients
// ═══════════════════════════════════════════════════════
function Clients({ clients, addClient, updateClient, deleteClient, invoices }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', contact_name: '', email: '', phone: '', address: '', notes: '' });
  const [editingId, setEditingId] = useState(null);

  const openNew = () => {
    setForm({ name: '', contact_name: '', email: '', phone: '', address: '', notes: '' });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setForm({ name: c.name || '', contact_name: c.contact_name || '', email: c.email || '', phone: c.phone || '', address: c.address || '', notes: c.notes || '' });
    setEditingId(c.id);
    setShowForm(true);
  };

  const saveClient = async () => {
    if (!form.name) return;
    if (editingId) {
      await updateClient(editingId, form);
    } else {
      await addClient(form);
    }
    setForm({ name: '', contact_name: '', email: '', phone: '', address: '', notes: '' });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 28, fontWeight: 400 }}>Clients</h1>
        <button className="btn btn-primary" onClick={openNew}>+ Add Client</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {clients.length === 0 ? (
          <div className="card" style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#8a8580', padding: 40 }}>No clients yet</div>
        ) : clients.map(c => {
          const cInv = invoices.filter(i => i.client_name === c.name);
          const total = cInv.filter(i => i.status === 'paid').reduce((s, i) => s + Number(i.total), 0);
          const outs = cInv.filter(i => i.status === 'unpaid').reduce((s, i) => s + Number(i.total), 0);
          return (
            <div className="card" key={c.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</div>
                  {c.contact_name && <div style={{ fontSize: 13, color: '#3a3a3a', marginTop: 2 }}>{c.contact_name}</div>}
                  {c.email && <div style={{ fontSize: 12, color: '#8a8580', marginTop: 2 }}>{c.email}</div>}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}>Edit</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: '#c85c5c' }} onClick={async () => { if (window.confirm('Delete this client?')) await deleteClient(c.id); }}>×</button>
                </div>
              </div>
              {c.phone && <div style={{ fontSize: 12, color: '#8a8580', marginBottom: 4 }}>Phone: {c.phone}</div>}
              <div style={{ display: 'flex', gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <div><div style={{ fontSize: 11, color: '#8a8580' }}>Invoices</div><div className="mono" style={{ fontWeight: 600 }}>{cInv.length}</div></div>
                <div><div style={{ fontSize: 11, color: '#8a8580' }}>Paid</div><div className="mono" style={{ fontWeight: 600, color: '#4a7c8a' }}>{fmt(total)}</div></div>
                {outs > 0 && <div><div style={{ fontSize: 11, color: '#8a8580' }}>Owed</div><div className="mono" style={{ fontWeight: 600, color: '#c8956c' }}>{fmt(outs)}</div></div>}
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => { setShowForm(false); setEditingId(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 400, marginBottom: 20 }}>{editingId ? 'Edit Client' : 'Add Client'}</h2>
            {[['Company / Client Name', 'name'], ['Key Contact Name', 'contact_name'], ['Email', 'email'], ['Phone', 'phone'], ['Address', 'address'], ['Notes', 'notes']].map(([label, key]) => (
              <div key={key} style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>{label}</label>
                <input className="input" value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={saveClient}>Save Client</button>
              <button className="btn btn-ghost" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Bills
// ═══════════════════════════════════════════════════════
function Bills({ bills, addBill, updateBill, deleteBill, addExpense }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(null);
  const [filter, setFilter] = useState('all');
  const [errors, setErrors] = useState({});

  const emptyForm = () => ({
    from_name: '', reference: '', amount: '', gst: '0', category: EXPENSE_CATS[0],
    date: today(), due_date: '', status: 'unpaid', paid_date: '', paid_via: PAYMENT_ACCOUNTS[0],
    is_recurring: false, notes: '', file_url: ''
  });

  const openNew = () => { setForm(emptyForm()); setErrors({}); setShowForm(true); };
  const openEdit = (b) => { setForm({ ...b }); setErrors({}); setShowForm(true); };

  const validate = () => {
    const e = {};
    if (!form.from_name) e.from_name = true;
    if (!form.amount || Number(form.amount) <= 0) e.amount = true;
    if (!form.date) e.date = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveBill = async () => {
    if (!validate()) return;
    const bill = { ...form, amount: Number(form.amount), gst: Number(form.gst || 0) };
    if (form.id) {
      await updateBill(form.id, bill);
    } else {
      await addBill(bill);
    }
    setShowForm(false); setForm(null);
  };

  const markPaid = async (bill) => {
    const paidVia = window.prompt('Paid via? (Credit Card / Cash)', 'Credit Card');
    if (paidVia === null) return;
    await updateBill(bill.id, { status: 'paid', paid_date: today(), paid_via: paidVia });
    // Auto-create expense
    await addExpense({
      description: `Bill: ${bill.from_name}${bill.reference ? ' — ' + bill.reference : ''}`,
      amount: Number(bill.amount),
      category: bill.category || 'Other',
      date: today(),
      paid_via: paidVia,
      notes: `Auto-created from bill. Ref: ${bill.reference || 'N/A'}`
    });
  };

  const markUnpaid = async (bill) => {
    await updateBill(bill.id, { status: 'unpaid', paid_date: null });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setForm(p => ({ ...p, file_url: reader.result, file_name: file.name }));
    reader.readAsDataURL(file);
  };

  const filtered = filter === 'all' ? bills : bills.filter(b => {
    if (filter === 'overdue') return b.status === 'unpaid' && b.due_date && b.due_date < today();
    if (filter === 'recurring') return b.is_recurring;
    return b.status === filter;
  });

  const totalOwed = bills.filter(b => b.status === 'unpaid').reduce((s, b) => s + Number(b.amount), 0);
  const reqStyle = (key) => errors[key] ? { borderColor: '#d4896a' } : {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 28, fontWeight: 400 }}>Bills</h1>
          {totalOwed > 0 && <p style={{ fontSize: 13, color: '#c8956c', marginTop: 2 }}>Total owing: <span className="mono" style={{ fontWeight: 600 }}>{fmt(totalOwed)}</span></p>}
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ New Bill</button>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, overflowX: 'auto' }}>
        {['all', 'unpaid', 'paid', 'overdue', 'recurring'].map(f => (
          <button key={f} className={`tab ${filter === f ? 'active' : ''}`} style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table>
          <thead><tr><th>From</th><th>Reference</th><th>Category</th><th>Date</th><th>Due</th><th style={{ textAlign: 'right' }}>Amount</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: '#8a8580', padding: 40 }}>No bills found</td></tr>
            ) : filtered.map(b => {
              const isOverdue = b.status === 'unpaid' && b.due_date && b.due_date < today();
              const st = isOverdue ? 'overdue' : b.status;
              return (
                <tr key={b.id}>
                  <td style={{ fontWeight: 500 }}>{b.from_name}{b.is_recurring && <span style={{ fontSize: 10, color: '#c8956c', marginLeft: 6 }}>↻</span>}</td>
                  <td style={{ color: '#8a8580', fontSize: 13 }}>{b.reference || '—'}</td>
                  <td style={{ color: '#8a8580', fontSize: 13 }}>{b.category || '—'}</td>
                  <td style={{ color: '#8a8580', fontSize: 13 }}>{b.date}</td>
                  <td style={{ color: '#8a8580', fontSize: 13 }}>{b.due_date || '—'}</td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 600, color: '#c8956c' }}>{fmt(b.amount)}</td>
                  <td><span className={`badge badge-${st}`}>{st}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {b.status === 'unpaid' && <button className="btn btn-ghost btn-sm" onClick={() => markPaid(b)}>Pay</button>}
                      {b.status === 'paid' && <button className="btn btn-ghost btn-sm" onClick={() => markUnpaid(b)}>Unpay</button>}
                      {b.file_url && <button className="btn btn-ghost btn-sm" style={{ color: '#4a7c8a' }} onClick={() => { const a = document.createElement('a'); a.href = b.file_url; a.download = b.file_name || 'bill'; a.click(); }}>File</button>}
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(b)}>Edit</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: '#c85c5c' }} onClick={async () => { if (window.confirm('Delete this bill?')) await deleteBill(b.id); }}>×</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && form && (
        <div className="modal-overlay" onClick={() => { setShowForm(false); setForm(null); }}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 400, marginBottom: 20 }}>{form.id ? 'Edit Bill' : 'New Bill'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>From (Supplier) *</label>
                <input className="input" style={reqStyle('from_name')} value={form.from_name} onChange={e => setForm(p => ({ ...p, from_name: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Reference</label>
                <input className="input" placeholder="Invoice # or ref" value={form.reference || ''} onChange={e => setForm(p => ({ ...p, reference: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Category</label>
                <select className="input" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  {EXPENSE_CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Amount ($) *</label>
                <input className="input" type="number" step="0.01" style={reqStyle('amount')} value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>GST ($)</label>
                <input className="input" type="number" step="0.01" value={form.gst} onChange={e => setForm(p => ({ ...p, gst: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Date *</label>
                <input className="input" type="date" style={reqStyle('date')} value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Due Date</label>
                <input className="input" type="date" value={form.due_date || ''} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Pay Via</label>
                <select className="input" value={form.paid_via} onChange={e => setForm(p => ({ ...p, paid_via: e.target.value }))}>
                  {PAYMENT_ACCOUNTS.map(a => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#3a3a3a', cursor: 'pointer', marginTop: 20 }}>
                  <input type="checkbox" checked={form.is_recurring || false} onChange={e => setForm(p => ({ ...p, is_recurring: e.target.checked }))} style={{ width: 16, height: 16, accentColor: '#c8956c' }} />
                  Recurring bill
                </label>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Notes</label>
                <input className="input" value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, color: '#8a8580', marginBottom: 4, display: 'block' }}>Attach File (invoice/receipt)</label>
                <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleFileUpload} style={{ fontSize: 13, color: '#3a3a3a' }} />
                {form.file_name && <p style={{ fontSize: 12, color: '#4a7c8a', marginTop: 4 }}>Attached: {form.file_name}</p>}
              </div>
            </div>
            {Object.keys(errors).length > 0 && <p style={{ fontSize: 12, color: '#d4896a', marginBottom: 12 }}>Please fill in all required fields (*).</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={saveBill}>Save Bill</button>
              <button className="btn btn-ghost" onClick={() => { setShowForm(false); setForm(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Reports
// ═══════════════════════════════════════════════════════
function Reports({ invoices, expenses }) {
  const [reportFY, setReportFY] = useState(fy(today()));
  const allFYs = [...new Set([...invoices.map(i => fy(i.date)), ...expenses.map(e => fy(e.date)), fy(today())])].sort();

  const fyInv = invoices.filter(i => fy(i.date) === reportFY);
  const fyExp = expenses.filter(e => fy(e.date) === reportFY);
  const paidInv = fyInv.filter(i => i.status === 'paid');
  const totalIncome = paidInv.reduce((s, i) => s + Number(i.total), 0);
  const totalExpenses = fyExp.reduce((s, e) => s + Number(e.amount), 0);

  const months = {};
  const fyStart = parseInt(reportFY.split('-')[0]);
  for (let m = 6; m < 18; m++) {
    const mm = m % 12;
    const yy = mm >= 6 ? fyStart : fyStart + 1;
    const key = `${yy}-${String(mm + 1).padStart(2, '0')}`;
    months[key] = { income: 0, expenses: 0, label: new Date(yy, mm).toLocaleString('default', { month: 'short', year: '2-digit' }) };
  }
  paidInv.forEach(i => { const k = i.date.slice(0, 7); if (months[k]) months[k].income += Number(i.total); });
  fyExp.forEach(e => { const k = e.date.slice(0, 7); if (months[k]) months[k].expenses += Number(e.amount); });
  const maxVal = Math.max(1, ...Object.values(months).map(m => Math.max(m.income, m.expenses)));

  const expByCat = {};
  fyExp.forEach(e => { expByCat[e.category] = (expByCat[e.category] || 0) + Number(e.amount); });
  const incByType = {};
  paidInv.forEach(i => { incByType[i.income_type] = (incByType[i.income_type] || 0) + Number(i.total); });

  const quarters = {};
  paidInv.forEach(i => { const q = qtr(i.date); quarters[q] = quarters[q] || { income: 0, expenses: 0 }; quarters[q].income += Number(i.total); });
  fyExp.forEach(e => { const q = qtr(e.date); quarters[q] = quarters[q] || { income: 0, expenses: 0 }; quarters[q].expenses += Number(e.amount); });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 28, fontWeight: 400 }}>Reports</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: '#8a8580' }}>Financial Year:</label>
          <select className="input" style={{ width: 'auto' }} value={reportFY} onChange={e => setReportFY(e.target.value)}>
            {allFYs.map(f => <option key={f}>{f}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={() => {
            const rows = Object.entries(months).map(([k, v]) => ({ Month: v.label, Income: v.income.toFixed(2), Expenses: v.expenses.toFixed(2), Net: (v.income - v.expenses).toFixed(2) }));
            rows.push({ Month: 'TOTAL', Income: totalIncome.toFixed(2), Expenses: totalExpenses.toFixed(2), Net: (totalIncome - totalExpenses).toFixed(2) });
            exportCSV(rows, `pnl-${reportFY}.csv`);
          }}>Export P&L CSV</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Profit & Loss — FY {reportFY}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, textAlign: 'center' }}>
          <div><div style={{ fontSize: 12, color: '#8a8580' }}>Total Income</div><div className="mono" style={{ fontSize: 28, fontWeight: 700, color: '#4a7c8a' }}>{fmt(totalIncome)}</div></div>
          <div><div style={{ fontSize: 12, color: '#8a8580' }}>Total Expenses</div><div className="mono" style={{ fontSize: 28, fontWeight: 700, color: '#c8956c' }}>{fmt(totalExpenses)}</div></div>
          <div><div style={{ fontSize: 12, color: '#8a8580' }}>Net Profit</div><div className="mono" style={{ fontSize: 28, fontWeight: 700, color: totalIncome - totalExpenses >= 0 ? '#10b981' : '#ef4444' }}>{fmt(totalIncome - totalExpenses)}</div></div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Monthly Breakdown</h3>
        <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 180 }}>
          {Object.entries(months).map(([k, m]) => (
            <div key={k} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{ width: '100%', display: 'flex', gap: 2, alignItems: 'flex-end', height: 140 }}>
                <div style={{ flex: 1, background: '#4a7c8a', borderRadius: '3px 3px 0 0', height: `${(m.income / maxVal) * 140}px`, minHeight: m.income > 0 ? 3 : 0 }} />
                <div style={{ flex: 1, background: '#c8956c', borderRadius: '3px 3px 0 0', height: `${(m.expenses / maxVal) * 140}px`, minHeight: m.expenses > 0 ? 3 : 0 }} />
              </div>
              <div style={{ fontSize: 10, color: '#8a8580', writingMode: 'vertical-rl', height: 36 }}>{m.label}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#1a1a1a' }}><div style={{ width: 10, height: 10, borderRadius: 2, background: '#4a7c8a' }} /> Income</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#1a1a1a' }}><div style={{ width: 10, height: 10, borderRadius: 2, background: '#c8956c' }} /> Expenses</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Expenses by Category</h3>
          {Object.keys(expByCat).length === 0 ? <p style={{ color: '#8a8580', fontSize: 13 }}>No expenses</p> :
            Object.entries(expByCat).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
              <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ width: 110, fontSize: 12, color: '#8a8580', flexShrink: 0 }}>{cat}</div>
                <div style={{ flex: 1, background: '#f0ece6', borderRadius: 3, height: 14, overflow: 'hidden' }}>
                  <div style={{ width: `${(amt / totalExpenses) * 100}%`, height: '100%', background: '#c8956c', borderRadius: 3 }} />
                </div>
                <div className="mono" style={{ fontSize: 12, width: 80, textAlign: 'right' }}>{fmt(amt)}</div>
              </div>
            ))
          }
        </div>
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Income by Type</h3>
          {Object.keys(incByType).length === 0 ? <p style={{ color: '#8a8580', fontSize: 13 }}>No income</p> :
            Object.entries(incByType).sort((a, b) => b[1] - a[1]).map(([type, amt]) => (
              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ width: 110, fontSize: 12, color: '#8a8580', flexShrink: 0 }}>{type}</div>
                <div style={{ flex: 1, background: '#f0ece6', borderRadius: 3, height: 14, overflow: 'hidden' }}>
                  <div style={{ width: `${(amt / totalIncome) * 100}%`, height: '100%', background: '#6366f1', borderRadius: 3 }} />
                </div>
                <div className="mono" style={{ fontSize: 12, width: 80, textAlign: 'right' }}>{fmt(amt)}</div>
              </div>
            ))
          }
        </div>
      </div>

      <div className="card">
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Quarterly Summary (for Tax)</h3>
        <table>
          <thead><tr><th>Quarter</th><th style={{ textAlign: 'right' }}>Income</th><th style={{ textAlign: 'right' }}>Expenses</th><th style={{ textAlign: 'right' }}>Net</th></tr></thead>
          <tbody>
            {['Q1', 'Q2', 'Q3', 'Q4'].map(q => {
              const d = quarters[q] || { income: 0, expenses: 0 };
              const net = d.income - d.expenses;
              return (
                <tr key={q}><td style={{ fontWeight: 500 }}>{q}</td>
                  <td className="mono" style={{ textAlign: 'right', color: '#4a7c8a' }}>{fmt(d.income)}</td>
                  <td className="mono" style={{ textAlign: 'right', color: '#c8956c' }}>{fmt(d.expenses)}</td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 600, color: net >= 0 ? '#10b981' : '#ef4444' }}>{fmt(net)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
